import React, { useState, useEffect, useRef } from 'react';
import { Check, MapPin, Search, X, AlertCircle, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useAddressLookup } from '@/hooks/useAddressLookup';
import type { GeocodeResult } from '@/lib/types/azure';

interface StructuredAddress {
  addressLine?: string;
  locality?: string; // City
  adminDistrict?: string; // State/Province
  adminDistrict2?: string; // County
  postalCode?: string;
  countryRegion?: string;
  countryRegionISO2?: string;
}

interface AddressLookupInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onAddressValidated?: (result: {
    formattedAddress: string;
    structuredAddress: StructuredAddress;
    coordinates?: { latitude: number; longitude: number };
    confidence?: number;
    placeId?: string;
  }) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function AddressLookupInput({
  value = '',
  onChange,
  onAddressValidated,
  placeholder = "Enter address...",
  disabled = false,
  className
}: AddressLookupInputProps) {
  const [validationStatus, setValidationStatus] = useState<'none' | 'validating' | 'valid' | 'invalid'>('none');
  const [lastValidatedAddress, setLastValidatedAddress] = useState<string>('');
  const [validationResult, setValidationResult] = useState<any>(null);

  const {
    validateAddress,
    geocodeAddress,
    isValidating,
    formatAddress,
  } = useAddressLookup({
    autoValidate: false,
    cacheResults: true,
  });

  // Reset validation status when user changes the address
  useEffect(() => {
    if (value !== lastValidatedAddress) {
      setValidationStatus('none');
      setValidationResult(null);
    }
  }, [value, lastValidatedAddress]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange?.(newValue);
  };

  const handleAcceptSuggestion = (suggestion: GeocodeResult) => {
    const formattedAddress = formatAddress(suggestion.address);
    onChange?.(formattedAddress);
    setValidationStatus('valid');
    setLastValidatedAddress(formattedAddress);
    setValidationResult({
      ...suggestion,
      suggestions: [suggestion],
      inputAddress: value,
      isExactMatch: true
    });

    if (onAddressValidated) {
      onAddressValidated({
        formattedAddress: formattedAddress,
        structuredAddress: {
          addressLine: suggestion.address.addressLine,
          locality: suggestion.address.locality,
          adminDistrict: suggestion.address.adminDistrict,
          adminDistrict2: suggestion.address.adminDistrict2,
          postalCode: suggestion.address.postalCode,
          countryRegion: suggestion.address.countryRegion,
          countryRegionISO2: suggestion.address.countryRegionISO2,
        },
        coordinates: {
          latitude: suggestion.position.lat,
          longitude: suggestion.position.lon,
        },
        confidence: suggestion.score,
        placeId: suggestion.id,
      });
    }
  };

  const handleValidateAddress = async () => {
    if (!value || value.length < 5) return;

    setValidationStatus('validating');
    try {
      // First try to geocode to get coordinates and suggestions
      const geocodeResults = await geocodeAddress(value);

      if (geocodeResults && geocodeResults.length > 0) {
        const topResult = geocodeResults[0];
        const formattedAddress = formatAddress(topResult.address);

        setValidationStatus('valid');
        setLastValidatedAddress(value);
        setValidationResult({
          ...topResult,
          suggestions: geocodeResults.slice(0, 5), // Keep top 5 suggestions
          inputAddress: value,
          isExactMatch: value.toLowerCase().trim() === formattedAddress.toLowerCase().trim()
        });

        if (onAddressValidated) {
          onAddressValidated({
            formattedAddress: formattedAddress,
            structuredAddress: {
              addressLine: topResult.address.addressLine,
              locality: topResult.address.locality,
              adminDistrict: topResult.address.adminDistrict,
              adminDistrict2: topResult.address.adminDistrict2,
              postalCode: topResult.address.postalCode,
              countryRegion: topResult.address.countryRegion,
              countryRegionISO2: topResult.address.countryRegionISO2,
            },
            coordinates: {
              latitude: topResult.position.lat,
              longitude: topResult.position.lon,
            },
            confidence: topResult.score,
            placeId: topResult.id,
          });
        }
      } else {
        // Fallback to validation-only
        const validationResult = await validateAddress(value);
        setLastValidatedAddress(value);
        setValidationStatus(validationResult.isValid ? 'valid' : 'invalid');

        // Create suggestion objects from validation result
        const suggestions = [];
        if (validationResult.isValid && validationResult.suggestedAddress) {
          // Create a suggestion object that matches GeocodeResult structure
          suggestions.push({
            id: 'validation-suggestion',
            address: validationResult.suggestedAddress,
            score: validationResult.confidence,
            position: { lat: 0, lon: 0 }, // No coordinates from validation-only
          });
        }

        // Also include corrections if available
        if (validationResult.corrections && Array.isArray(validationResult.corrections)) {
          validationResult.corrections.forEach((correction, index) => {
            if (correction.address) {
              suggestions.push({
                id: `correction-${index}`,
                address: correction.address,
                score: correction.confidence || validationResult.confidence,
                position: { lat: 0, lon: 0 },
              });
            }
          });
        }

        setValidationResult({
          inputAddress: value,
          isExactMatch: false,
          validationOnly: true,
          suggestions: suggestions.slice(0, 5), // Keep top 5 suggestions
          ...validationResult
        });

        if (validationResult.isValid && onAddressValidated && validationResult.suggestedAddress) {
          onAddressValidated({
            formattedAddress: formatAddress(validationResult.suggestedAddress),
            structuredAddress: {
              addressLine: validationResult.suggestedAddress.addressLine,
              locality: validationResult.suggestedAddress.locality,
              adminDistrict: validationResult.suggestedAddress.adminDistrict,
              adminDistrict2: validationResult.suggestedAddress.adminDistrict2,
              postalCode: validationResult.suggestedAddress.postalCode,
              countryRegion: validationResult.suggestedAddress.countryRegion,
              countryRegionISO2: validationResult.suggestedAddress.countryRegionISO2,
            },
            confidence: validationResult.confidence,
          });
        }
      }
    } catch (error) {
      console.error('Address validation failed:', error);
      setValidationStatus('invalid');
    }
  };

  const getValidationIcon = () => {
    switch (validationStatus) {
      case 'validating':
        return <Search className="h-4 w-4 animate-spin text-blue-500" />;
      case 'valid':
        return <Check className="h-4 w-4 text-green-500" />;
      case 'invalid':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <MapPin className="h-4 w-4 text-gray-400" />;
    }
  };

  const getValidationColor = () => {
    switch (validationStatus) {
      case 'valid':
        return 'border-green-300 focus:border-green-500';
      case 'invalid':
        return 'border-red-300 focus:border-red-500';
      case 'validating':
        return 'border-blue-300 focus:border-blue-500';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled || isValidating}
          className={cn(
            'flex-1',
            getValidationColor(),
            className
          )}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleValidateAddress}
          disabled={isValidating || !value || value.length < 5}
          className="px-3 whitespace-nowrap"
        >
          {isValidating ? (
            <>
              <Search className="h-4 w-4 animate-spin mr-1" />
              Validating...
            </>
          ) : (
            <>
              <MapPin className="h-4 w-4 mr-1" />
              Validate
            </>
          )}
        </Button>
      </div>

      {/* Validation Status Message */}
      {validationStatus !== 'none' && (
        <div className="flex items-center gap-2 text-xs">
          {getValidationIcon()}
          {validationStatus === 'valid' && (
            <div>
              <span className="text-green-600 font-medium">Address validated</span>
            </div>
          )}
          {validationStatus === 'invalid' && (
            <span className="text-red-600">Address could not be validated</span>
          )}
          {validationStatus === 'validating' && (
            <span className="text-blue-600">Validating address...</span>
          )}
        </div>
      )}

      {/* Address Suggestions from geocoding results */}
      {validationStatus === 'valid' && validationResult && !validationResult.isExactMatch && validationResult.suggestions && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground">
            Suggested corrections:
          </div>
          {validationResult.suggestions.map((suggestion: GeocodeResult, index: number) => {
            const formattedSuggestion = formatAddress(suggestion.address);
            const isTopSuggestion = index === 0;

            return (
              <div
                key={suggestion.id || index}
                className={cn(
                  "flex items-center justify-between p-2 border rounded text-xs",
                  isTopSuggestion ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-gray-200"
                )}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {formattedSuggestion}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {isTopSuggestion && (
                      <span className="text-blue-600 text-xs">Best match</span>
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleAcceptSuggestion(suggestion)}
                  className="ml-2 h-8 px-2 text-xs"
                >
                  <ArrowRight className="h-3 w-3 mr-1" />
                  Use
                </Button>
              </div>
            );
          })}
        </div>
      )}


      {/* Formatted Address Display (when exact match or after accepting suggestion) */}
      {validationStatus === 'valid' && validationResult && validationResult.isExactMatch && (
        <div className="text-xs text-green-600 bg-green-50 p-2 rounded border border-green-200">
          <div className="flex items-center gap-2">
            <Check className="h-3 w-3" />
            <strong>Address confirmed:</strong> {formatAddress(validationResult.address || validationResult.suggestedAddress)}
          </div>
        </div>
      )}

      {/* Show current formatted address if validation successful but no exact match */}
      {validationStatus === 'valid' && validationResult && !validationResult.isExactMatch && !validationResult.suggestions?.length && (
        <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded border">
          <strong>Formatted:</strong> {formatAddress(validationResult.address || validationResult.suggestedAddress)}
        </div>
      )}
    </div>
  );
}
